

import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, uploadFile } from "../../../Utils/FileUpload";
import { Submit, AddBrandTitle, BrandTitle } from "../../../Utils/Constants";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import useCustomToasts from "../../../hooks/useCustomToast";
import "react-toastify/dist/ReactToastify.css";

interface FormValues {
  brand_name: string;
  logo: FileList;
}
interface ErrorResponse {
  message: string;
}

export default function AddBrands() {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      const file = data.logo?.[0];

      if (!file) {
        toast.error("Please upload a logo.");
        return;
      }

      const fileName = await uploadFile(file);

      const payload = {
        ...data,
        logo: fileName,
      };

      const token = localStorage.getItem("token");

      const response = await axios.post(`${baseUrl}brand/store`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        SuccessToast("Data added successfully", "/view_brands");
      } else {
        ErrorToast("Failed to add");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        (axiosError.response?.data as ErrorResponse)?.message ||
        "An unexpected error occurred";

      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={AddBrandTitle}
        parent={BrandTitle}
        title={AddBrandTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={12}>
                  <Label htmlFor="brand-name">Brand Name</Label>
                  <Controller
                    name="brand_name"
                    control={control}
                    rules={{
                      required: "Brand name is required",
                     
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="brand-name"
                          type="text"
                          placeholder="Brand name"
                          {...field}
                          invalid={!!errors.brand_name}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.brand_name ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.brand_name && (
                          <FormFeedback>
                            {errors.brand_name.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="formFile">Logo</Label>
                  <Controller
                    name="logo"
                    control={control}
                    rules={{ required: "Logo is required" }}
                    render={({ field: { onChange, onBlur, ref } }) => (
                      <>
                        <Input
                          id="formImage"
                          type="file"
                          accept="image/*"
                          onChange={(e) => onChange(e.target.files)}
                          onBlur={onBlur}
                          innerRef={ref}
                          invalid={!!errors.logo}
                          style={{ borderColor: errors.logo ? "red" : "" }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.logo && (
                          <FormFeedback>{errors.logo.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {Submit}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
